import {
	ArrowLeft,
	Eye,
	FileText,
	Mail,
	MapPin,
	Phone,
	Plus,
	User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Button,
	CopyButton,
	EmptyState,
	Notification,
	useNotification,
} from "../../components/FormComponents";
import StatusBadge from "../../components/StatusBadge";
import { useClientDetail } from "../../hooks/useClientDetail";
import { formatCurrency, formatDate } from "../../utils/format";

export default function ClientDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { notification, showNotification } = useNotification();
	const {
		clientData,
		proposals,
		acceptedProposals,
		rejectedProposals,
		pendingProposals,
		totalAcceptedValue,
		loading,
	} = useClientDetail(id, showNotification);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<p className="text-slate-300">Carregando...</p>
			</div>
		);
	}

	if (!clientData) {
		return (
			<div className="text-center py-12">
				<p className="text-slate-400">Cliente não encontrado</p>
				<Button
					type="button"
					onClick={() => navigate("/admin/clients")}
					variant="secondary"
					className="mt-4"
				>
					Voltar
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between flex-wrap gap-4">
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={() => navigate("/admin/clients")}
						className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
						aria-label="Voltar para a lista de clientes"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div>
						<h1 className="text-2xl font-bold text-white">{clientData.name}</h1>
						<p className="text-slate-400 text-sm">{clientData.email}</p>
					</div>
				</div>
				<Button type="button" onClick={() => navigate("/admin/proposals/new")}>
					<Plus className="w-4 h-4 inline mr-2" />
					Nova Proposta
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Client Info */}
				<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
					<h2 className="text-lg font-semibold text-white mb-4">
						Dados do Cliente
					</h2>
					<div className="space-y-4">
						<div className="flex items-center gap-3">
							<User className="w-4 h-4 text-slate-400" />
							<div>
								<p className="text-xs text-slate-500">Nome</p>
								<p className="text-sm text-white">{clientData.name}</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<Mail className="w-4 h-4 text-slate-400" />
							<div>
								<p className="text-xs text-slate-500">Email</p>
								<p className="text-sm text-slate-300">
									{clientData.email || "-"}
								</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<Phone className="w-4 h-4 text-slate-400" />
							<div>
								<p className="text-xs text-slate-500">Telefone</p>
								<p className="text-sm text-slate-300">
									{clientData.phone || "-"}
								</p>
							</div>
						</div>
						{clientData.cnpj && (
							<div className="flex items-center gap-3">
								<FileText className="w-4 h-4 text-slate-400" />
								<div>
									<p className="text-xs text-slate-500">CNPJ</p>
									<p className="text-sm text-slate-300">{clientData.cnpj}</p>
								</div>
							</div>
						)}
						{clientData.address && (
							<div className="flex items-center gap-3">
								<MapPin className="w-4 h-4 text-slate-400" />
								<div>
									<p className="text-xs text-slate-500">Endereço</p>
									<p className="text-sm text-slate-300">{clientData.address}</p>
								</div>
							</div>
						)}
					</div>

					{/* Stats */}
					<div className="mt-6 pt-4 border-t border-slate-700 space-y-3">
						<div className="flex justify-between">
							<span className="text-xs text-slate-500">Total de propostas</span>
							<span className="text-sm text-white font-bold">
								{proposals.length}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-xs text-slate-500">Aceitas</span>
							<span className="text-sm text-green-400 font-bold">
								{acceptedProposals.length}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-xs text-slate-500">Rejeitadas</span>
							<span className="text-sm text-red-400 font-bold">
								{rejectedProposals.length}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-xs text-slate-500">Pendentes</span>
							<span className="text-sm text-amber-400 font-bold">
								{pendingProposals.length}
							</span>
						</div>
						<div className="flex justify-between pt-2 border-t border-slate-700">
							<span className="text-xs text-slate-500">
								Valor total aceitas
							</span>
							<span className="text-sm text-cyan-400 font-bold">
								{formatCurrency(totalAcceptedValue)}
							</span>
						</div>
					</div>
				</div>

				{/* Proposals List */}
				<div className="lg:col-span-2 bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
					<h2 className="text-lg font-semibold text-white mb-4">
						Histórico de Propostas
					</h2>

					{proposals.length === 0 ? (
						<EmptyState
							icon={FileText}
							message="Nenhuma proposta encontrada"
							description="Crie uma nova proposta para este cliente"
						/>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead className="bg-slate-800/50 border-b border-slate-700">
									<tr>
										<th className="px-4 py-3 text-left font-medium text-slate-300">
											Proposta
										</th>
										<th className="px-4 py-3 text-left font-medium text-slate-300">
											Empresa
										</th>
										<th className="px-4 py-3 text-left font-medium text-slate-300">
											Status
										</th>
										<th className="px-4 py-3 text-left font-medium text-slate-300">
											Valor
										</th>
										<th className="px-4 py-3 text-left font-medium text-slate-300">
											Data
										</th>
										<th
											className="px-4 py-3 text-left font-medium text-slate-300"
											aria-label="Ações"
										/>
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-700">
									{proposals.map((p, idx) => (
										<tr
											key={p.id || `client-prop-${idx}`}
											className="hover:bg-slate-800/30 transition"
										>
											<td className="px-4 py-3">
												<span className="font-mono text-cyan-300 font-bold">
													{p.proposal_number}
												</span>
												{p.title && (
													<p className="text-xs text-slate-500 mt-0.5 truncate max-w-[150px]">
														{p.title}
													</p>
												)}
											</td>
											<td className="px-4 py-3 text-slate-400">
												{p.company_name}
											</td>
											<td className="px-4 py-3">
												<StatusBadge status={p.status} />
											</td>
											<td className="px-4 py-3 text-green-400 font-bold">
												{formatCurrency(p.total_value)}
											</td>
											<td className="px-4 py-3 text-slate-500 text-xs">
												{formatDate(p.created_at)}
											</td>
											<td className="px-4 py-3">
												<div className="flex gap-2">
													<button
														type="button"
														onClick={() => navigate(`/admin/proposals/${p.id}`)}
														className="px-3 py-1 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 rounded text-xs transition-all flex items-center gap-1"
														aria-label={`Ver proposta ${p.proposal_number}`}
													>
														<Eye className="w-3 h-3" /> Ver
													</button>
													{p.access_token && (
														<CopyButton
															text={`${window.location.origin}/p/${p.access_token}`}
															label="Link"
															size="sm"
														/>
													)}
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
